import { noop } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';

export interface DropdownItemMeta {
  hasSubmenu: boolean;
  openSubmenu?: () => void;
  closeSubmenu?: () => void;
  focusSubmenuList?: () => void;
}

interface DropdownItemMetaInternal extends DropdownItemMeta {
  siblingIndex: number;
}

interface DropdownListContextValue {
  isActive: boolean;
  activeId: string | undefined;
  registerItem: (id: string, meta: DropdownItemMeta) => void;
  unregisterItem: (id: string) => void;
  setHighlightFromHover: (id: string) => void;
  isHighlighted: (id: string) => boolean;
  highlightFirst: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
  listRef: RefObject<HTMLDivElement | null>;
}

const neverHighlighted = (_id: string) => false;

const defaultListRef: RefObject<HTMLDivElement | null> = { current: null };

const defaultDropdownListContextValue: DropdownListContextValue = {
  isActive: false,
  activeId: undefined,
  registerItem: noop,
  unregisterItem: noop,
  setHighlightFromHover: noop,
  isHighlighted: neverHighlighted,
  highlightFirst: noop,
  onKeyDown: noop,
  listRef: defaultListRef,
};

const DropdownListContext = createContext<DropdownListContextValue>(defaultDropdownListContextValue);

export function useDropdownListContext() {
  return useContext(DropdownListContext);
}

interface DropdownListContextProviderProps {
  children: ReactNode;
  onArrowLeftToParent?: () => void;
}

export function DropdownListContextProvider({ children, onArrowLeftToParent }: DropdownListContextProviderProps) {
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const activeIdRef = useRef<string | undefined>(undefined);
  const listRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef(new Map<string, DropdownItemMetaInternal>());

  const setActiveIdAndRef = useCallback((id: string | undefined) => {
    activeIdRef.current = id;
    setActiveId(id);
  }, []);

  activeIdRef.current = activeId;

  const registerItem = useCallback((id: string, meta: DropdownItemMeta) => {
    const items = itemsRef.current;
    const existing = items.get(id);
    const siblingIndex = existing !== undefined ? existing.siblingIndex : items.size;
    items.set(id, { ...meta, siblingIndex });
  }, []);

  const unregisterItem = useCallback((id: string) => {
    itemsRef.current.delete(id);
  }, []);

  const closeOtherSubmenus = useCallback((exceptId?: string) => {
    itemsRef.current.forEach((meta, itemId) => {
      if (itemId !== exceptId) {
        meta.closeSubmenu?.();
      }
    });
  }, []);

  const highlightFirst = useCallback(() => {
    const ids = getOrderedItemIds(itemsRef.current);
    if (ids.length > 0) {
      setActiveIdAndRef(ids[0]);
    }
  }, [setActiveIdAndRef]);

  const navigateToSibling = useCallback(
    (direction: 1 | -1) => {
      const itemIds = getOrderedItemIds(itemsRef.current);
      if (itemIds.length === 0) {
        return;
      }

      const currentIndex = activeIdRef.current !== undefined ? itemIds.indexOf(activeIdRef.current) : -1;
      const nextIndex =
        direction === 1
          ? currentIndex < itemIds.length - 1
            ? currentIndex + 1
            : 0
          : currentIndex > 0
            ? currentIndex - 1
            : itemIds.length - 1;
      const nextId = itemIds[nextIndex];

      closeOtherSubmenus(nextId);
      setActiveIdAndRef(nextId);
      const { openSubmenu } = itemsRef.current.get(nextId) ?? {};
      if (openSubmenu) {
        queueMicrotask(openSubmenu);
      }
    },
    [setActiveIdAndRef, closeOtherSubmenus]
  );

  const setHighlightFromHover = useCallback(
    (id: string) => {
      closeOtherSubmenus(id);
      setActiveIdAndRef(id);
      itemsRef.current.get(id)?.openSubmenu?.();
    },
    [setActiveIdAndRef, closeOtherSubmenus]
  );

  const isHighlighted = useCallback((id: string) => activeId === id, [activeId]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (itemsRef.current.size === 0) {
        return;
      }

      const currentActiveId = activeIdRef.current;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          e.stopPropagation();
          navigateToSibling(1);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          e.stopPropagation();
          navigateToSibling(-1);
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          e.stopPropagation();
          if (currentActiveId === undefined) {
            break;
          }
          const meta = itemsRef.current.get(currentActiveId);
          if (meta?.openSubmenu) {
            meta.openSubmenu();
            if (meta.focusSubmenuList) {
              queueMicrotask(meta.focusSubmenuList);
            }
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          e.stopPropagation();
          onArrowLeftToParent?.();
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          e.stopPropagation();
          if (currentActiveId !== undefined) {
            const doc = listRef.current?.ownerDocument ?? document;
            const el = doc.querySelector(`[data-dropdown-item-id="${CSS.escape(currentActiveId)}"]`);
            if (el instanceof HTMLElement) {
              el.click();
            }
          }
          break;
        }
      }
    },
    [navigateToSibling, onArrowLeftToParent]
  );

  const value = useMemo<DropdownListContextValue>(
    () => ({
      isActive: true,
      activeId,
      registerItem,
      unregisterItem,
      setHighlightFromHover,
      isHighlighted,
      highlightFirst,
      onKeyDown,
      listRef,
    }),
    [activeId, registerItem, unregisterItem, setHighlightFromHover, isHighlighted, highlightFirst, onKeyDown]
  );

  return <DropdownListContext.Provider value={value}>{children}</DropdownListContext.Provider>;
}

function getOrderedItemIds(items: Map<string, DropdownItemMetaInternal>): string[] {
  return [...items.entries()].toSorted(([, a], [, b]) => a.siblingIndex - b.siblingIndex).map(([id]) => id);
}
